import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismButton } from '@mylighthouse/prism-ember';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import { PRODUCT_NAMES } from 'frontend-utils/utils/constants/constant-values';
import { DRAWER_IDS } from 'lumen/utils/constants/general-constants';
import { SMART_INSIGHTS_TOUR_ID } from 'lumen/utils/constants/smart-insights';

import styles from './smart-insights-navigation-button.module.css';

interface GeneralSmartInsightsSmartInsightsNavigationButtonSignature {
  Args: {
    toggleSmartInsightsDrawer?: () => void;
  };
  Element: HTMLButtonElement;
}

export default class GeneralSmartInsightsSmartInsightsNavigationButton extends Component<GeneralSmartInsightsSmartInsightsNavigationButtonSignature> {
  @service declare drawer: Services['drawer'];
  @service declare intercom: Services['intercom'];
  @service declare smartInsights: Services['smart-insights'];
  @service declare usageAnalytics: Services['usage-analytics'];

  get showNotificationBubble(): boolean {
    const isDrawerExpanded = this.drawer.isDrawerExpanded(
      DRAWER_IDS.SMART_INSIGHTS_DRAWER,
    );

    if (!this.smartInsights.localStorageHotelOrMarketId) {
      return (
        this.smartInsights.hasInsights &&
        !this.smartInsights.hasDismissedNotificationBubble &&
        !isDrawerExpanded
      );
    }

    const lastViewedDict = this.smartInsights.getHasSeenDictFromLocalStorage();

    const lastViewedDate =
      lastViewedDict[this.smartInsights.localStorageHotelOrMarketId];

    if (!lastViewedDate) {
      return (
        this.smartInsights.hasInsights &&
        !this.smartInsights.hasDismissedNotificationBubble &&
        !isDrawerExpanded
      );
    }

    const lastViewedDay = dayjs(lastViewedDate).startOf('day');
    const lastViewedDayIsInPast =
      lastViewedDay.isValid() && lastViewedDay.isBefore(dayjs().startOf('day'));

    return (
      this.smartInsights.hasInsights &&
      !this.smartInsights.hasDismissedNotificationBubble &&
      !isDrawerExpanded &&
      lastViewedDayIsInPast
    );
  }

  @action onClick(): void {
    this.toggleSmartInsightsDrawer();

    if (this.smartInsights.hasInsights) {
      this.smartInsights.storeHasSeenDictInLocalStorage();
    }
  }

  @action toggleSmartInsightsDrawer(): void {
    const { toggleSmartInsightsDrawer } = this.args;

    toggleSmartInsightsDrawer?.();

    if (this.drawer.isDrawerExpanded(DRAWER_IDS.SMART_INSIGHTS_DRAWER)) {
      this.intercom.startTour(SMART_INSIGHTS_TOUR_ID);

      this.usageAnalytics.track('Smart Insights Viewed', {
        eventTrigger: 'smart-insights-calendar',
        product: 'smart-insights',
      });

      this.usageAnalytics.initialiseSessionReplay({
        product: PRODUCT_NAMES.PRODUCT_SMART_INSIGHTS,
      });
    }
  }

  <template>
    <PrismButton
      @buttonType="intelligence-secondary"
      @iconBefore="smart-insights"
      @label={{t "smart_insights.title"}}
      @onClick={{this.onClick}}
      class="{{styles.smart-insights-navigation-button}} relative"
      data-test-id="smart-insights-button"
    >
      {{#if this.showNotificationBubble}}
        <div
          class="{{styles.notification-bubble}} absolute"
          data-test-id="smart-insights-button-notification-bubble"
        />
      {{/if}}
    </PrismButton>
  </template>
}
